import { ReactNode, Ref, forwardRef, useImperativeHandle, useRef } from 'react';
import { Platform } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import * as PopoverPrimitive from '@rn-primitives/popover';
import { styles } from './Popover.styles';

export type PopoverProps = {
  content: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
  sideOffset?: number;
  autoCloseDelay?: number;
  onPress?: () => void;
};

export const Popover = forwardRef(
  (
    {
      children,
      content,
      side = 'top',
      sideOffset = 8,
      align = 'center',
      autoCloseDelay,
      onPress,
    }: PopoverProps,
    ref: Ref<Pick<PopoverPrimitive.TriggerRef, 'open' | 'close'>>
  ) => {
    const popoverRef = useRef<PopoverPrimitive.TriggerRef>(null);
    const container = useRef(
      Platform.OS === 'web'
        ? document.getElementById('primitives-root')
        : undefined
    );

    useImperativeHandle(ref, () => ({
      open: () => {
        popoverRef.current?.open();
      },
      close: () => {
        popoverRef.current?.close();
      },
    }));

    const onOpenChange = (open: boolean) => {
      if (open && autoCloseDelay) {
        setTimeout(() => {
          popoverRef.current?.close();
        }, autoCloseDelay);
      }
    };

    return (
      <PopoverPrimitive.Root onOpenChange={onOpenChange}>
        <PopoverPrimitive.Trigger ref={popoverRef} onPress={onPress}>
          {children}
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal container={container.current} hostName="root">
          <PopoverPrimitive.Content
            side={side}
            sideOffset={sideOffset}
            align={align}
            className={styles.content}
          >
            <BodyText
              size="sm"
              context="provider"
              color="neutral10"
              className={styles.text}
            >
              {content}
            </BodyText>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);

Popover.displayName = 'Popover';
